'use client';

// VIRTUALIZED VERSION (currently commented out)
// import HtmlLinkHandlerVirtualized from '@/components/ui/html-link-handler-virtualized';
// import type { HtmlTreeNode } from '@/lib/html-parsing/parseHtmlTree';

// NON-VIRTUALIZED VERSION (currently active)
import HtmlLinkHandler from '@/components/ui/html-link-handler';

import { Card, CardContent } from '@/components/ui/card';
import LegislationDecisionDetector from '@/components/ui/legislation-decision-detector';
import { Skeleton } from '@/components/ui/skeleton';
import getLegislationHtmlServices from '@/services/get-legislation-html-services';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

interface LegislationContentProps {
  segments: string[];
  /**
   * Statute body fetched during SSR. When present the text is already in the
   * server-rendered HTML — which is the entire point: crawlers index HTML
   * cheaply but ration JavaScript rendering, and this route has ~4.6M sibling
   * URLs competing for that budget. `null` means the server fetch was skipped
   * or failed, and the client-side fetch below still runs unchanged.
   */
  initialHtml?: string | null;
  // VIRTUALIZED VERSION props (commented out)
  // htmlTree?: HtmlTreeNode[] | null;
  // iri?: string | null;
}

/**
 * Component for displaying legislation content with section highlighting functionality
 * Currently using NON-VIRTUALIZED version (HtmlLinkHandler)
 * To switch back to virtualized: uncomment virtualized imports and code blocks
 */
export function LegislationContent({
  segments,
  initialHtml = null /* , htmlTree, iri */,
}: LegislationContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isHtmlLoading, setIsHtmlLoading] = useState<boolean>(
    initialHtml === null
  );
  const [, setError] = useState<string | null>(null);

  const t = useTranslations('LegislationDetails');

  // NON-VIRTUALIZED VERSION: state for HTML content.
  // Seeded from the server render so the first paint (and the crawler's view)
  // already contains the statute text.
  const [htmlContent, setHtmlContent] = useState<string>(initialHtml ?? '');

  // VIRTUALIZED VERSION state (commented out)
  // const [localIri, setLocalIri] = useState<string>(iri || '');
  // const [localHtmlTree, setLocalHtmlTree] = useState<HtmlTreeNode[] | null>(
  //   htmlTree || null
  // );

  // NON-VIRTUALIZED VERSION: Fetch HTML content.
  // Skipped when the server already provided it — refetching the same document
  // on mount would double the bandwidth and blank the text mid-hydration.
  useEffect(() => {
    if (initialHtml !== null) {
      return;
    }

    if (segments.length === 5) {
      const fetchHtmlContent = async () => {
        setIsHtmlLoading(true);
        try {
          const iriStr = segments.join('/');

          const html =
            await getLegislationHtmlServices.getLegislationHTML(iriStr);

          setHtmlContent(html);
        } catch (error) {
          console.error('[LegislationContent] ❌ Failed to fetch HTML:', error);
          setError(error as string);
        } finally {
          setIsHtmlLoading(false);
        }
      };
      fetchHtmlContent();
    }
  }, [segments, initialHtml]);

  // VIRTUALIZED VERSION: Fetch and parse tree (commented out)
  // useEffect(() => {
  //   if (!htmlTree && segments.length === 5) {
  //     const fetchHtmlContent = async () => {
  //       setIsHtmlLoading(true);
  //       try {
  //         const iriStr = segments.join('/');
  //         setLocalIri(iriStr);
  //
  //         console.log('[Client] ⚠️ No server tree, fetching HTML client-side');
  //
  //         const htmlContent = await getLegislationHtmlServices.getLegislationHTML(iriStr);
  //
  //         console.log(
  //           '[Client] ✅ Fetched HTML, size:',
  //           htmlContent.length,
  //           'chars'
  //         );
  //
  //         const { parseHtmlIntoTree } = await import(
  //           '@/lib/html-parsing/parseHtmlTree'
  //         );
  //
  //         const parsedTree = parseHtmlIntoTree(htmlContent);
  //
  //         console.log(
  //           '[Client] 🌳 Parsed tree client-side:',
  //           parsedTree.length,
  //           'roots'
  //         );
  //
  //         setLocalHtmlTree(parsedTree);
  //       } catch (error) {
  //         console.error('[Client] ❌ Failed to fetch/parse HTML:', error);
  //         setError(error as string);
  //       } finally {
  //         setIsHtmlLoading(false);
  //       }
  //     };
  //     fetchHtmlContent();
  //   } else if (htmlTree) {
  //     console.log(
  //       '[Client] ✅ Using server-parsed tree:',
  //       htmlTree.length,
  //       'roots'
  //     );
  //   }
  // }, [htmlTree, segments]);

  // VIRTUALIZED VERSION variables (commented out)
  // const treeToRender = htmlTree || localHtmlTree;
  // const iriToUse = iri || localIri;

  if (isHtmlLoading) {
    return (
      // `min-h-screen` is load-bearing, not decoration. This branch only runs
      // when the server fetch missed its deadline, and it is replaced by a
      // statute body that is always taller than the viewport. A skeleton card
      // shorter than the content it stands in for guarantees a layout shift at
      // the swap; reserving a viewport's worth means the real body grows
      // downward from the fold instead of pushing the visible rows around,
      // which is the difference CLS actually measures.
      <div className='my-8 space-y-6 w-full max-w-5xl mx-auto px-4 sm:px-6 min-h-screen'>
        {/* Content skeleton */}
        <Card className='w-full shadow-sm'>
          <CardContent className='p-6'>
            <div className='space-y-5'>
              <Skeleton className='h-7 w-3/4 animate-pulse' />
              <div className='space-y-3 mt-4'>
                <Skeleton className='h-5 w-full animate-pulse' />
                <Skeleton className='h-5 w-full animate-pulse' />
                <Skeleton className='h-5 w-full animate-pulse' />
              </div>
              <div className='space-y-3 mt-6'>
                <Skeleton className='h-7 w-2/3 animate-pulse' />
                <Skeleton className='h-5 w-full animate-pulse' />
                <Skeleton className='h-5 w-full animate-pulse' />
                <Skeleton className='h-5 w-full animate-pulse' />
              </div>
              <div className='space-y-3 mt-6'>
                <Skeleton className='h-7 w-3/4 animate-pulse' />
                <Skeleton className='h-5 w-full animate-pulse' />
                <Skeleton className='h-5 w-full animate-pulse' />
                <Skeleton className='h-5 w-full animate-pulse' />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div ref={contentRef} className='legislation-content relative pr-24'>
      <div className='ms-12'>
        {/* NON-VIRTUALIZED VERSION (currently active) */}
        {htmlContent ? (
          <>
            {/*
              Desktop-only, but the viewport test lives inside the component
              rather than here. Gating the mount on a `useState(false)` that an
              effect corrected meant every phone mounted it, ran its full
              document scan, and then unmounted it — paying the cost on the one
              device that never shows the badges.
            */}
            <LegislationDecisionDetector iri={segments.join('/')} />
            <HtmlLinkHandler htmlContent={htmlContent} />
          </>
        ) : (
          <p>{t('noContent')}</p>
        )}

        {/* VIRTUALIZED VERSION (commented out) */}
        {/* {treeToRender ? (
          <>
            {!isMobile && <LegislationDecisionDetector iri={iriToUse} />}
            <HtmlLinkHandlerVirtualized trees={treeToRender} iri={iriToUse} />
          </>
        ) : (
          <p>{t('noContent')}</p>
        )} */}
      </div>
    </div>
  );
}
